import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

/* ── Contratos (backend: manufacturing.dto.ts + manufacturing-extended.dto.ts) ─
 *
 * GET  /manufacturing/boms/:id        -> BomResponseDto (inclui `labor[]` no detalhe)
 * PUT  /manufacturing/boms/:id/labor  body: SetBomLaborRequestDto { labor: BomLaborLineDto[] }
 *                                     -> { bomId, labor, laborCost }
 * GET  /manufacturing/boms/:id/costs  -> BomCostBreakdownResponseDto
 * GET  /manufacturing/boms/:id/attachments        -> BomAttachmentResponseDto[]
 * POST /manufacturing/boms/:id/attachments        body: CreateBomAttachmentRequestDto
 * DELETE /manufacturing/boms/:id/attachments/:attachmentId -> 204
 * GET  /manufacturing/boms/:id/revisions          -> BomRevisionResponseDto[]
 * POST /manufacturing/boms/:id/revisions          body: CreateBomRevisionRequestDto
 * POST /manufacturing/boms/:id/revisions/:revisionId/approve -> BomRevisionResponseDto
 */

/* ── BOM com mao de obra (detalhe) ── */

export interface BomLaborLine {
  description: string;
  /** EXECUCAO | SETUP | INSPECAO | EMBALAGEM ... */
  type: string;
  /** Horas da operacao por unidade produzida. */
  qtyHours: number;
  /** Custo por hora (R$/h). */
  costRate: number;
  totalCost: number;
  leadTimeDays?: number | null;
}

export interface BomDetail {
  id: string;
  productId: string;
  version: number;
  description: string;
  status: string;
  components: {
    productId: string;
    productName?: string;
    sku?: string;
    quantity: number;
    unit: string;
    unitCost: number;
    wastePercent: number;
    notes?: string | null;
  }[];
  labor?: BomLaborLine[];
  totalCost: number;
  effectiveSince: string;
  effectiveUntil?: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useBomDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['manufacturing', 'boms', id],
    queryFn: () => apiClient.get<BomDetail>(`/manufacturing/boms/${id}`),
    enabled: !!id,
  });
}

export function useSetBomLabor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, labor }: { id: string; labor: BomLaborLine[] }) =>
      apiClient.put<{ bomId: string; labor: BomLaborLine[]; laborCost: number }>(
        `/manufacturing/boms/${id}/labor`,
        { labor },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['manufacturing', 'boms', vars.id] });
      qc.invalidateQueries({ queryKey: ['manufacturing', 'boms', vars.id, 'costs'] });
      qc.invalidateQueries({ queryKey: ['manufacturing', 'boms'] });
    },
  });
}

/* ── Custos detalhados ── */

export interface BomCostLine {
  name: string;
  /** MATERIAL | LABOR */
  type: string;
  unitCost: number;
  /** Qtd (componentes) ou horas (mao de obra). */
  qtyOrTime: number;
  totalCost: number;
}

export interface BomCostBreakdown {
  bomId: string;
  version: number;
  lines: BomCostLine[];
  materialCost: number;
  laborCost: number;
  totalCost: number;
  currency?: string;
}

export function useBomCosts(id: string | undefined) {
  return useQuery({
    queryKey: ['manufacturing', 'boms', id, 'costs'],
    queryFn: () => apiClient.get<BomCostBreakdown>(`/manufacturing/boms/${id}/costs`),
    enabled: !!id,
  });
}

/* ── Anexos tecnicos ── */

export interface BomAttachment {
  id: string;
  bomId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  storageKey: string;
  uploadedById?: string | null;
  createdAt: string;
}

export interface CreateAttachmentInput {
  fileName: string;
  fileSize: number;
  mimeType?: string;
  storageKey: string;
}

export function useBomAttachments(id: string | undefined) {
  return useQuery({
    queryKey: ['manufacturing', 'boms', id, 'attachments'],
    queryFn: () => apiClient.get<BomAttachment[]>(`/manufacturing/boms/${id}/attachments`),
    enabled: !!id,
  });
}

export function useCreateBomAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & CreateAttachmentInput) =>
      apiClient.post<BomAttachment>(`/manufacturing/boms/${id}/attachments`, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['manufacturing', 'boms', vars.id, 'attachments'] });
    },
  });
}

export function useDeleteBomAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, attachmentId }: { id: string; attachmentId: string }) =>
      apiClient.delete(`/manufacturing/boms/${id}/attachments/${attachmentId}`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['manufacturing', 'boms', vars.id, 'attachments'] });
    },
  });
}

/* ── Historico de revisoes ── */

export interface BomRevisionChange {
  type: string;
  description: string;
  before?: string | number | null;
  after?: string | number | null;
}

export interface BomRevision {
  id: string;
  bomId: string;
  version: string;
  status: string;
  changes: BomRevisionChange[];
  approvedById?: string | null;
  approvedAt?: string | null;
  createdAt: string;
}

export interface CreateRevisionInput {
  version: string;
  changes: BomRevisionChange[];
}

export function useBomRevisions(id: string | undefined) {
  return useQuery({
    queryKey: ['manufacturing', 'boms', id, 'revisions'],
    queryFn: () => apiClient.get<BomRevision[]>(`/manufacturing/boms/${id}/revisions`),
    enabled: !!id,
  });
}

export function useCreateBomRevision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & CreateRevisionInput) =>
      apiClient.post<BomRevision>(`/manufacturing/boms/${id}/revisions`, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['manufacturing', 'boms', vars.id, 'revisions'] });
      qc.invalidateQueries({ queryKey: ['manufacturing', 'boms', vars.id] });
    },
  });
}

export function useApproveBomRevision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, revisionId }: { id: string; revisionId: string }) =>
      apiClient.post<BomRevision>(`/manufacturing/boms/${id}/revisions/${revisionId}/approve`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['manufacturing', 'boms', vars.id, 'revisions'] });
      qc.invalidateQueries({ queryKey: ['manufacturing', 'boms', vars.id] });
    },
  });
}